import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ChevronRight,
  Copy,
  Plus,
  Loader2,
  ExternalLink,
  Save,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import {
  adminFetchProjects,
  adminCreateProject,
  adminUpdateProject,
  adminFetchLeads,
} from "@/lib/api";
import { ADMIN } from "@/constants/testIds";

const PACKAGES = [
  { id: "starter", name: "Starter — $99/mo" },
  { id: "business", name: "Business — $149/mo" },
  { id: "premium", name: "Premium — $199/mo" },
];

function buildTrackUrl(token) {
  return `${window.location.origin}/track/${token}`;
}

function copyToClipboard(text) {
  if (navigator.clipboard) {
    navigator.clipboard.writeText(text).then(
      () => toast.success("Tracking link copied!"),
      () => toast.error("Copy failed.")
    );
  } else {
    toast.error("Clipboard not available.");
  }
}

export default function ProjectsTab({ password }) {
  const [projects, setProjects] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [editProj, setEditProj] = useState(null);

  const refresh = async () => {
    try {
      setLoading(true);
      const [proj, ld] = await Promise.all([
        adminFetchProjects(password),
        adminFetchLeads(password).catch(() => []),
      ]);
      setProjects(proj);
      setLeads(ld);
    } catch (e) {
      toast.error("Could not load projects.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-zinc-400">
          Active customer projects auto-create when a Stripe payment succeeds.
        </p>
        <Button
          data-testid={ADMIN.projectNewBtn}
          onClick={() => setNewOpen(true)}
          className="btn-neon rounded-none"
        >
          <Plus className="w-4 h-4 mr-2" /> New Project
        </Button>
      </div>

      <div className="bento-card overflow-hidden" data-testid={ADMIN.projectsTable}>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent border-white/10">
              <TableHead className="text-zinc-400">Customer</TableHead>
              <TableHead className="text-zinc-400">Package</TableHead>
              <TableHead className="text-zinc-400">Stage</TableHead>
              <TableHead className="text-zinc-400">Progress</TableHead>
              <TableHead className="text-zinc-400">ETA</TableHead>
              <TableHead className="text-zinc-400">Tracking Link</TableHead>
              <TableHead className="text-zinc-400 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12">
                  <Loader2 className="w-5 h-5 mx-auto animate-spin text-[#CCFF00]" />
                </TableCell>
              </TableRow>
            ) : projects.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-zinc-500 py-12">
                  No projects yet.
                </TableCell>
              </TableRow>
            ) : (
              projects.map((p) => {
                const stages = p.stages || [];
                const completed = stages.filter(
                  (s) => s.status === "completed"
                ).length;
                const total = stages.length || 1;
                const percent = Math.round((completed / total) * 100);
                const currentStage =
                  stages.find((s) => s.status === "in_progress")?.name ||
                  (p.status === "completed" ? "Completed" : stages[0]?.name);
                return (
                  <TableRow key={p.id} className="border-white/5 hover:bg-white/5">
                    <TableCell>
                      <div className="font-medium">{p.customer_name}</div>
                      <div className="text-xs text-zinc-500">{p.customer_email}</div>
                    </TableCell>
                    <TableCell>{p.package_name}</TableCell>
                    <TableCell>
                      <span className="px-2 py-1 rounded-full bg-[#CCFF00]/10 text-[#CCFF00] text-xs font-mono-jb">
                        {currentStage}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                          <div
                            className="h-full bg-[#CCFF00]"
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                        <span className="text-xs font-mono-jb text-zinc-400">
                          {percent}%
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-zinc-400 text-xs font-mono-jb">
                      {p.eta ? new Date(p.eta).toLocaleDateString() : "—"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <button
                          data-testid={ADMIN.projectCopyLink(p.id)}
                          onClick={() => copyToClipboard(buildTrackUrl(p.tracking_token))}
                          className="text-zinc-400 hover:text-[#CCFF00] flex items-center gap-1 text-xs font-mono-jb"
                          title="Copy customer tracking link"
                        >
                          <Copy className="w-3.5 h-3.5" />
                          /track/{p.tracking_token?.slice(0, 6)}…
                        </button>
                        <a
                          href={`/track/${p.tracking_token}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-zinc-400 hover:text-[#CCFF00]"
                          title="Open"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        data-testid={ADMIN.projectEditBtn(p.id)}
                        onClick={() => setEditProj(p)}
                        size="sm"
                        className="btn-outline-neon rounded-none h-8"
                      >
                        Edit
                        <ChevronRight className="w-3 h-3 ml-1" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <NewProjectDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        password={password}
        onCreated={refresh}
        leads={leads}
      />
      <EditProjectDialog
        proj={editProj}
        onClose={() => setEditProj(null)}
        password={password}
        onUpdated={refresh}
      />
    </div>
  );
}

function NewProjectDialog({ open, onOpenChange, password, onCreated, leads = [] }) {
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    business_name: "",
    package_id: "",
    eta: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const prefillFromLead = (leadId) => {
    const l = leads.find((x) => x.id === leadId);
    if (!l) return;
    setForm((s) => ({
      ...s,
      customer_name: l.name || s.customer_name,
      customer_email: l.email || s.customer_email,
      business_name: l.business_name || s.business_name,
      package_id: l.selected_build_package || s.package_id,
      message: l.project_details
        ? `Welcome aboard! Your project notes:\n${l.project_details}`
        : s.message,
    }));
    toast.success(`Prefilled from ${l.name}`);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.customer_email) {
      toast.error("Name and email are required.");
      return;
    }
    try {
      setLoading(true);
      const proj = await adminCreateProject({ password, ...form });
      toast.success("Project created!");
      copyToClipboard(buildTrackUrl(proj.tracking_token));
      onOpenChange(false);
      setForm({
        customer_name: "",
        customer_email: "",
        business_name: "",
        package_id: "",
        eta: "",
        message: "",
      });
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create project.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0a] border-[#27272A] text-white sm:max-w-[520px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            New Project
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Manually create a customer project. A unique tracking link will be generated and copied.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2">
          {leads.length > 0 && (
            <div>
              <Label className="text-xs uppercase tracking-widest font-mono-jb text-[#CCFF00]">
                Prefill from existing customer
              </Label>
              <Select onValueChange={prefillFromLead}>
                <SelectTrigger
                  data-testid="admin-new-project-prefill"
                  className="mt-1 bg-[#111] border-[#27272A] focus:ring-[#CCFF00]"
                >
                  <SelectValue placeholder="Pick a lead to autofill fields…" />
                </SelectTrigger>
                <SelectContent className="bg-[#111] border-[#27272A] text-white max-h-[300px]">
                  {leads.map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.name} — {l.email}
                      {l.business_name ? ` · ${l.business_name}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-xs text-zinc-500">
                Or skip and enter details manually below.
              </p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest font-mono-jb">
                Customer Name *
              </Label>
              <Input
                data-testid="admin-new-project-name"
                value={form.customer_name}
                onChange={(e) =>
                  setForm({ ...form, customer_name: e.target.value })
                }
                className="mt-1 bg-[#111] border-[#27272A] focus-visible:ring-[#CCFF00]"
                required
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest font-mono-jb">
                Email *
              </Label>
              <Input
                data-testid="admin-new-project-email"
                type="email"
                value={form.customer_email}
                onChange={(e) =>
                  setForm({ ...form, customer_email: e.target.value })
                }
                className="mt-1 bg-[#111] border-[#27272A] focus-visible:ring-[#CCFF00]"
                required
              />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-mono-jb">
              Business Name
            </Label>
            <Input
              value={form.business_name}
              onChange={(e) =>
                setForm({ ...form, business_name: e.target.value })
              }
              className="mt-1 bg-[#111] border-[#27272A] focus-visible:ring-[#CCFF00]"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest font-mono-jb">
                Package
              </Label>
              <Select
                value={form.package_id}
                onValueChange={(v) => setForm({ ...form, package_id: v })}
              >
                <SelectTrigger className="mt-1 bg-[#111] border-[#27272A]">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent className="bg-[#111] border-[#27272A] text-white">
                  {PACKAGES.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest font-mono-jb">
                Target Launch (ETA)
              </Label>
              <Input
                type="date"
                value={form.eta}
                onChange={(e) => setForm({ ...form, eta: e.target.value })}
                className="mt-1 bg-[#111] border-[#27272A] focus-visible:ring-[#CCFF00]"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-mono-jb">
              Welcome Message
            </Label>
            <Textarea
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="mt-1 bg-[#111] border-[#27272A] focus-visible:ring-[#CCFF00] min-h-[80px]"
              placeholder="Optional message shown on the customer's tracking page."
            />
          </div>
          <Button
            data-testid="admin-new-project-submit"
            type="submit"
            disabled={loading}
            className="btn-neon rounded-none w-full h-11 mt-4"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Project"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditProjectDialog({ proj, onClose, password, onUpdated }) {
  const [stageIdx, setStageIdx] = useState(0);
  const [eta, setEta] = useState("");
  const [message, setMessage] = useState("");
  const [stageNote, setStageNote] = useState("");
  const [previewLinks, setPreviewLinks] = useState([]);
  const [linkLabel, setLinkLabel] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (proj) {
      setStageIdx(proj.current_stage_index || 0);
      setEta(proj.eta ? proj.eta.slice(0, 10) : "");
      setMessage(proj.message || "");
      setStageNote(proj.stages?.[proj.current_stage_index || 0]?.note || "");
      setPreviewLinks(proj.preview_links || []);
    }
  }, [proj]);

  if (!proj) return null;

  const stages = proj.stages || [];

  const addLink = () => {
    if (!linkUrl) return;
    setPreviewLinks([...previewLinks, { label: linkLabel || linkUrl, url: linkUrl }]);
    setLinkLabel("");
    setLinkUrl("");
  };

  const removeLink = (i) => {
    setPreviewLinks(previewLinks.filter((_, idx) => idx !== i));
  };

  const save = async () => {
    try {
      setLoading(true);
      await adminUpdateProject({
        password,
        project_id: proj.id,
        current_stage_index: stageIdx,
        eta: eta || null,
        message: message,
        stage_note: stageNote,
        preview_links: previewLinks,
      });
      toast.success("Project updated!");
      onUpdated();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={!!proj} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0a0a0a] border-[#27272A] text-white sm:max-w-[640px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {proj.customer_name} — {proj.package_name}
          </DialogTitle>
          <DialogDescription className="text-zinc-400 font-mono-jb text-xs">
            /track/{proj.tracking_token}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          <div>
            <Label className="text-xs uppercase tracking-widest font-mono-jb">
              Current Stage
            </Label>
            <Select
              value={String(stageIdx)}
              onValueChange={(v) => setStageIdx(parseInt(v, 10))}
            >
              <SelectTrigger className="mt-1 bg-[#111] border-[#27272A]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#111] border-[#27272A] text-white">
                {stages.map((s, i) => (
                  <SelectItem key={i} value={String(i)}>
                    {i + 1}. {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-2 text-xs text-zinc-500">
              Moving the stage forward marks all earlier stages as complete.
            </p>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest font-mono-jb">
              Note for Current Stage
            </Label>
            <Textarea
              value={stageNote}
              onChange={(e) => setStageNote(e.target.value)}
              className="mt-1 bg-[#111] border-[#27272A] focus-visible:ring-[#CCFF00] min-h-[70px]"
              placeholder="e.g. Awaiting your feedback on homepage v2"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest font-mono-jb">
                Target Launch (ETA)
              </Label>
              <Input
                type="date"
                value={eta}
                onChange={(e) => setEta(e.target.value)}
                className="mt-1 bg-[#111] border-[#27272A] focus-visible:ring-[#CCFF00]"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest font-mono-jb">
              Message for Customer
            </Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="mt-1 bg-[#111] border-[#27272A] focus-visible:ring-[#CCFF00] min-h-[70px]"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest font-mono-jb">
              Preview Links / Files
            </Label>
            <div className="mt-2 space-y-2">
              {previewLinks.map((l, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between gap-2 bg-[#111] border border-[#27272A] rounded-lg px-3 py-2"
                >
                  <div className="text-sm truncate">
                    <span className="font-semibold">{l.label}</span>
                    <span className="text-zinc-500 ml-2 font-mono-jb text-xs truncate">
                      {l.url}
                    </span>
                  </div>
                  <button onClick={() => removeLink(i)} className="text-red-400 hover:text-red-300">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <div className="flex gap-2">
                <Input
                  value={linkLabel}
                  onChange={(e) => setLinkLabel(e.target.value)}
                  placeholder="Label"
                  className="bg-[#111] border-[#27272A] focus-visible:ring-[#CCFF00]"
                />
                <Input
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://…"
                  className="bg-[#111] border-[#27272A] focus-visible:ring-[#CCFF00]"
                />
                <Button onClick={addLink} type="button" className="btn-outline-neon rounded-none">
                  Add
                </Button>
              </div>
            </div>
          </div>

          <Button
            data-testid="admin-edit-project-save"
            onClick={save}
            disabled={loading}
            className="btn-neon rounded-none w-full h-11"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" /> Save Changes
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
